import { MediumConfig } from 'modules/frequencyManagement/types';
import {
  FrequencyMode,
  FrequencyPeriod,
  FrequencyManagementMessageType,
} from 'modules/frequencyManagement/lib/types';
import { sortMessageTypes } from 'modules/frequencyManagement/lib/utils/mediumDomainUtils';
import {
  MODE,
  CAPPING_PERIOD,
  CAPPING_MAX_ALLOWED,
  OPTIMIZE_PERIOD,
  OPTIMIZE_MIN_ALLOWED,
  OPTIMIZE_MAX_ALLOWED,
  MESSAGE_TYPES,
  ID,
  INDEX,
  NAME,
  CHANNEL_NAME,
  INITIAL_MODE,
} from './FormProvider.constants';

const EMPTY_CAPPING_VALUES = {
  [CAPPING_PERIOD]: FrequencyPeriod.Daily,
  [CAPPING_MAX_ALLOWED]: undefined,
};

const EMPTY_OPTIMIZE_VALUES = {
  [OPTIMIZE_PERIOD]: FrequencyPeriod.Weekly,
  [OPTIMIZE_MIN_ALLOWED]: undefined,
  [OPTIMIZE_MAX_ALLOWED]: undefined,
};

const EMPTY_FREQUENCY_VALUES = {
  ...EMPTY_CAPPING_VALUES,
  ...EMPTY_OPTIMIZE_VALUES,
};

const EMPTY_MEDIUM_VALUES = {
  [MODE]: FrequencyMode.Off,
  ...EMPTY_FREQUENCY_VALUES,
  [MESSAGE_TYPES]: [],
};

const deriveInitialMode = (messageType: FrequencyManagementMessageType) => {
  const { frequencyManagement } = messageType;

  /** ignoreCap must be mapped to the client-side-only "NoCap FrequencyMode"  */
  if (frequencyManagement.ignoreCap) return FrequencyMode.NoCap;

  /**
   * If no frequency management settings are currently configured, this is functionally
   * equivalent to the "Off FrequencyMode".
   */
  if (!frequencyManagement.settings || !frequencyManagement.settings.mode) return FrequencyMode.Off;

  return frequencyManagement.settings.mode;
};

const createStaticValuesForMessageType = (
  messageType: FrequencyManagementMessageType,
  index: number,
) => {
  const { id, name, messageChannel } = messageType;

  const initialMode = deriveInitialMode(messageType);

  return {
    [ID]: id,
    [INDEX]: index,
    [NAME]: name,
    [CHANNEL_NAME]: messageChannel.name,
    [INITIAL_MODE]: initialMode,
  };
};

const createDynamicValuesForMessageType = (messageType: FrequencyManagementMessageType) => {
  const { settings, ignoreCap } = messageType.frequencyManagement;

  if (ignoreCap)
    /** If ignoreCap is true, settings may not be present, so this check must be first. */
    return {
      ...EMPTY_FREQUENCY_VALUES,
      [MODE]: FrequencyMode.NoCap,
    };

  if (!settings || settings.mode === FrequencyMode.Off)
    return {
      ...EMPTY_FREQUENCY_VALUES,
      [MODE]: FrequencyMode.Off,
    };

  if (settings.mode === FrequencyMode.Cap) {
    return {
      ...EMPTY_FREQUENCY_VALUES,
      [MODE]: settings.mode,
      [CAPPING_PERIOD]: settings.period as FrequencyPeriod,
      [CAPPING_MAX_ALLOWED]: settings.maxAllowed as number,
    };
  }

  if (settings.mode === FrequencyMode.Optimize) {
    return {
      ...EMPTY_FREQUENCY_VALUES,
      [MODE]: settings.mode,
      [OPTIMIZE_PERIOD]: settings.period as FrequencyPeriod,
      [OPTIMIZE_MIN_ALLOWED]: settings.minAllowed as number,
      [OPTIMIZE_MAX_ALLOWED]: settings.maxAllowed as number,
    };
  }
};

export const createDefaultValuesForMessageType = (
  messageType: FrequencyManagementMessageType,
  index: number,
) => ({
  ...createStaticValuesForMessageType(messageType, index),
  ...createDynamicValuesForMessageType(messageType),
});

export const createDefaultValues = (mediumConfig: MediumConfig) => {
  const { settings, messageTypes = [] } = mediumConfig;

  /**
   * We must ensure that for any given visit to the Frequency Management page,
   * message types have a set ordering that is determined before the form values
   * are initialized. Otherwise, because order is not guaranteed on the fetch-
   * and upsert- graphql requests, it is possible for message types to be re-
   * ordered upon successful upsert.
   */
  const messageTypesSorted = sortMessageTypes(messageTypes);

  if (settings === null) return EMPTY_MEDIUM_VALUES;

  const { mode, period, minAllowed, maxAllowed } = settings;

  if (mode === FrequencyMode.Off) {
    return {
      ...EMPTY_MEDIUM_VALUES,
      [MODE]: mode,
      [MESSAGE_TYPES]: messageTypesSorted.map(createDefaultValuesForMessageType),
    };
  }

  if (mode === FrequencyMode.Cap) {
    return {
      ...EMPTY_MEDIUM_VALUES,
      [MODE]: mode,
      [CAPPING_PERIOD]: period as FrequencyPeriod,
      [CAPPING_MAX_ALLOWED]: maxAllowed as number,
      [MESSAGE_TYPES]: messageTypesSorted.map(createDefaultValuesForMessageType),
    };
  }

  if (mode === FrequencyMode.Optimize) {
    return {
      ...EMPTY_MEDIUM_VALUES,
      [MODE]: mode,
      [OPTIMIZE_PERIOD]: period as FrequencyPeriod,
      [OPTIMIZE_MIN_ALLOWED]: minAllowed as number,
      [OPTIMIZE_MAX_ALLOWED]: maxAllowed as number,
      [MESSAGE_TYPES]: messageTypesSorted.map(createDefaultValuesForMessageType),
    };
  }
};
